import { useContext, useEffect, useState } from 'react';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { store } from 'components/Store';
import CreateATopicPage from './CreateATopicPage';
import { useNavigate } from 'react-router-dom';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useHandleChangeKeywords from 'components/Settings/Topics/Helpers/handeChangeKeywords';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import ConfigData from 'config/ConfigData';

const SignUpCreateATopic = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { parliamentarianOnboarding, activePlan } = state;
  const { maxNumKeywordsPerTeam } = activePlan;

  const source = new AbortController();
  const { generalApiCall } = useGeneralApiCall();

  const [keywords, setKeywords] = useState([]);
  const [topicName, setTopicName] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientWebsite, setClientWebsite] = useState('');
  const [clientDescription, setClientDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agencyStep, setAgencyStep] = useState('createTopic');
  const [error, setError] = useState({ type: '', message: '' });
  const [remainingKeywords, setRemainingKeywords] = useState(
    maxNumKeywordsPerTeam === null ? 'unlimited' : maxNumKeywordsPerTeam
  );
  const { handleChangeKeywords } = useHandleChangeKeywords();
  const { normalizeUrl } = useLogoUrlReplacement();

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      source.abort('Create topic cancelled by the user.');
    };
  }, []);

  const createFreeTrialPolicyInfluence = async () => {
    let pathname = `/api/subscription/start-trial`;
    let method = 'post';
    let requestProperties = {
      website: 'PolicyMogul',
    };
    if (activePlan?.name === 'Policy Influence') {
      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: true,
        returnCompleteRequest: true,
      });
      if (results?.status !== 200) {
        return false;
      }
    }
    return true;
  };

  const createTeam = async () => {
    setIsLoading(true);
    try {
      const pathname = '/api/team';
      const method = 'post';
      const logoUrl = await loadLogoUrl(clientWebsite);
      let requestProperties = {
        teamName: clientName,
        organisationWebsite: clientWebsite,
        organisationLogo: logoUrl,
      };
      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: true,
        returnCompleteRequest: true,
      });
      if (results?.status !== 200) {
        return false;
      }
      return true;
    } catch (error) {}
  };

  const createFirstList = async () => {
    setIsLoading(true);
    try {
      let verifyTrialPolicyInfluence = await createFreeTrialPolicyInfluence();
      if (verifyTrialPolicyInfluence) {
        let createList = await generalApiCall({
          pathname: '/api/keyword-list/create',
          method: 'post',
          requestProperties: {
            name: topicName,
            keywords: keywords,
            countries: ConfigData.defaultCountries,
          },
          needsAuthentication: true,
          requestSource: source,
          returnCompleteRequest: true,
        });

        if (createList.status === 200) {
          navigate('/create-your-profile/4');
        } else {
          createNotification('danger', 'There was a technical problem, please try again');
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
      createNotification('danger', 'There was a technical problem, please try again');
    }
  };

  const handleChange = (newKeywords, changed, changedIndexes) => {
    setError({ type: '', message: '' });
    //SEPARATED BY COMMAS
    const trimmedKeywords = handleChangeKeywords({ newKeywords, changed, changedIndexes });

    setKeywords(trimmedKeywords);
    //CALCULATE REMAINING KEYWORDS
    if (remainingKeywords !== 'unlimited') {
      let totalKeywords = trimmedKeywords.length;
      setRemainingKeywords(maxNumKeywordsPerTeam - totalKeywords);
    }
  };

  const loadLogoUrl = async (value) => {
    return new Promise((resolve, reject) => {
      const logo = new Image();
      const normalizedValue = normalizeUrl(value);
      const url = `https://img.logo.dev/${normalizedValue}`;
      const finalUrl = `${url}?token=${import.meta.env.VITE_APP_LOGO_DOT_DEV_TOKEN}&fallback=404`;

      logo.onload = () => {
        resolve(url);
      };

      logo.onerror = () => {
        resolve(null);
      };

      logo.src = finalUrl;
    });
  };

  return (
    <CreateATopicPage
      stateProperties={{ parliamentarianOnboarding, keywords, activePlan }}
      errorProperties={{ error, setError }}
      topicProperties={{ topicName, setTopicName }}
      clientProperties={{
        clientName,
        setClientName,
        clientWebsite,
        setClientWebsite,
        clientDescription,
        setClientDescription,
        agencyStep,
        setAgencyStep,
      }}
      remainingKeywords={remainingKeywords}
      handleChange={handleChange}
      createFirstList={createFirstList}
      createTeam={createTeam}
      isLoading={isLoading}
    />
  );
};

export default SignUpCreateATopic;
